/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get(
    {
      searchQuery,
      inboxId,
      status,
      assigneeType,
      page,
      labels,
      teamId,
      conversationType,
      sortBy,
      updatedWithin,
    },
    options = {}
  ) {
    return axios.get(this.url, {
      signal: options.signal,
      params: {
        q: searchQuery,
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
      },
    });
  }

  filter(payload, options = {}) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      signal: options.signal,
      params: {
        sort_by: payload.sortBy,
        page: payload.page,
        q: payload.q,
        ...payload.extraParams,
      },
    });
  }

  getContactInfoRequest(conversationId) {
    return axios.get(`${this.url}/${conversationId}/contact_info_request`);
  }

  requestContactInfo(conversationId) {
    return axios.post(`${this.url}/${conversationId}/contact_info_request`);
  }

  filterMeta(payload) {
    return axios.post(`${this.url}/filter_meta`, payload);
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({
    conversationId,
    status,
    snoozedUntil = null,
    skipCsat = false,
  }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
      skip_csat: skipCsat,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  markReading({ conversationId }) {
    return axios.post(`${this.url}/${conversationId}/mark_as_reading`);
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  blockDomain(conversationId) {
    return axios.post(`${this.url}/${conversationId}/block_domain`);
  }

  unblockDomain(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unblock_domain`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  requestCopilot(conversationId, body) {
    return axios.post(`${this.url}/${conversationId}/copilot`, body);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  translate(conversationId, targetLanguage) {
    return axios.post(`${this.url}/${conversationId}/translate`, {
      target_language: targetLanguage,
    });
  }

  merge(conversationId, targetConversationId) {
    return axios.post(`${this.url}/${conversationId}/merge`, {
      target_conversation_id: targetConversationId,
    });
  }

  batchMerge(targetConversationDisplayId, sourceConversationDisplayIds) {
    return axios.post(`${this.url}/batch_merge`, {
      target_conversation_id: targetConversationDisplayId,
      source_conversation_ids: sourceConversationDisplayIds,
    });
  }

  move(conversationId, targetInboxId) {
    return axios.post(`${this.url}/${conversationId}/move`, {
      target_inbox_id: targetInboxId,
    });
  }

  movableInboxes(conversationId) {
    return axios.get(`${this.url}/${conversationId}/movable_inboxes`);
  }
}

export default new ConversationApi();
